import { Extension } from '@tiptap/core';
import type MarkdownIt from 'markdown-it';
import type StateCore from 'markdown-it/lib/rules_core/state_core';

const FENCE_REGEX = /^\s*(`{3,}|~{3,})\s*.*$/;
const INDENTED_HTML_LINE_REGEX = /^([ ]{4,}|[ ]{0,3}\t+)(?=<)/;
// XSS 위험 태그(script, iframe, object, embed, form, input, link, meta, style) 제외
// 콘텐츠 표현용 안전 태그만 허용
const SAFE_HTML_TAGS = [
    // 블록 레이아웃
    'div',
    'p',
    'section',
    'article',
    'aside',
    'header',
    'footer',
    'main',
    'nav',
    'figure',
    'figcaption',
    'details',
    'summary',
    // 제목
    'h[1-6]',
    // 테이블
    'table',
    'thead',
    'tbody',
    'tfoot',
    'tr',
    'th',
    'td',
    'caption',
    'colgroup',
    'col',
    // 목록
    'ul',
    'ol',
    'li',
    'dl',
    'dt',
    'dd',
    // 인라인 서식
    'span',
    'a',
    'strong',
    'b',
    'em',
    'i',
    'u',
    's',
    'del',
    'ins',
    'mark',
    'small',
    'sub',
    'sup',
    'abbr',
    'cite',
    'code',
    'kbd',
    'var',
    'samp',
    // 미디어·기타
    'img',
    'br',
    'hr',
    'blockquote',
    'pre',
    'address',
    'time',
    'ruby',
    'rt',
    'rp',
];
const ALLOWED_HTML_TAG_REGEX = new RegExp(`^<\\/?(${SAFE_HTML_TAGS.join('|')})(?:\\s|>|\\/)`, 'i');

const normalizeIndentedHtmlLines = (markdown: string): string => {
    if (!markdown) return markdown;

    const lines = markdown.split(/\r?\n/);
    const out: string[] = [];

    let inFence = false;
    let fenceChar = '';
    let fenceLen = 0;

    for (const line of lines) {
        const fenceMatch = FENCE_REGEX.exec(line);
        if (fenceMatch) {
            const fence = fenceMatch[1];
            const char = fence[0];

            if (!inFence) {
                inFence = true;
                fenceChar = char;
                fenceLen = fence.length;
            } else if (char === fenceChar && fence.length >= fenceLen) {
                inFence = false;
                fenceChar = '';
                fenceLen = 0;
            }

            out.push(line);
            continue;
        }

        if (inFence) {
            out.push(line);
            continue;
        }

        const quoteMatch = /^((?:>\s*)+)/.exec(line);
        const quotePrefix = quoteMatch ? quoteMatch[1] : '';
        const rest = quotePrefix ? line.slice(quotePrefix.length) : line;

        const match = INDENTED_HTML_LINE_REGEX.exec(rest);
        if (!match) {
            out.push(line);
            continue;
        }

        const afterIndent = rest.slice(match[1].length);
        if (!ALLOWED_HTML_TAG_REGEX.test(afterIndent)) {
            out.push(line);
            continue;
        }

        out.push(`${quotePrefix}${afterIndent}`);
    }

    return out.join('\n');
};

export const IndentHtmlNormalizer = Extension.create({
    name: 'indentHtmlNormalizer',

    addStorage() {
        return {
            markdown: {
                parse: {
                    setup(md: MarkdownIt) {
                        md.core.ruler.after('normalize', 'normalize_indented_html_lines', (state: StateCore) => {
                            state.src = normalizeIndentedHtmlLines(state.src);
                        });
                    },
                },
            },
        };
    },
});
